import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { useTranslation } from 'react-i18next';
import { getServices } from '../firebase/db';
import { FaTools, FaHeadset, FaWrench, FaCogs, FaCheckCircle, FaShippingFast, FaAward } from 'react-icons/fa';
import './Services.css';

const Services = () => {
  const { t } = useTranslation();
  const [services, setServices] = useState([]);
  const [loading, setLoading] = useState(true);

  // Map string icon names from DB to actual react-icons
  const getIcon = (iconName) => {
    switch (iconName) {
      case 'FaTools': return <FaTools className="service-icon" />;
      case 'FaHeadset': return <FaHeadset className="service-icon" />;
      case 'FaWrench': return <FaWrench className="service-icon" />;
      case 'FaCogs': return <FaCogs className="service-icon" />;
      case 'FaShippingFast': return <FaShippingFast className="service-icon" />;
      case 'FaAward': return <FaAward className="service-icon" />;
      default: return <FaCheckCircle className="service-icon" />;
    }
  };

  const getServiceTitle = (service) => {
    const lower = (service.title || '').toLowerCase();
    if (lower.includes('conveyor') || lower.includes('material')) return t('svcConveyorTitle');
    if (lower.includes('export') || lower.includes('logistic')) return t('svcExportTitle');
    if (lower.includes('consult') || lower.includes('project')) return t('svcConsultTitle');
    if (lower.includes('install')) return t('svcInstallTitle');
    if (lower.includes('maint') || lower.includes('after-sales') || lower.includes('after sales')) return t('svcMaintTitle');
    if (lower.includes('part') || lower.includes('spare')) return t('svcPartsTitle');
    if (lower.includes('train')) return t('svcTrainingTitle');
    if (lower.includes('custom')) return t('svcCustomTitle');
    if (lower.includes('support') || lower.includes('24/7')) return t('svcSupportTitle');
    return t(service.title, service.title);
  };

  const getServiceDesc = (service) => {
    const lower = (service.title || '').toLowerCase();
    if (lower.includes('conveyor') || lower.includes('material')) return t('svcConveyorDesc');
    if (lower.includes('export') || lower.includes('logistic')) return t('svcExportDesc');
    if (lower.includes('consult') || lower.includes('project')) return t('svcConsultDesc');
    if (lower.includes('install')) return t('svcInstallDesc');
    if (lower.includes('maint') || lower.includes('after-sales') || lower.includes('after sales')) return t('svcMaintDesc');
    if (lower.includes('part') || lower.includes('spare')) return t('svcPartsDesc');
    if (lower.includes('train')) return t('svcTrainingDesc');
    if (lower.includes('custom')) return t('svcCustomDesc');
    if (lower.includes('support') || lower.includes('24/7')) return t('svcSupportDesc');
    return t(service.description, service.description);
  };

  const fallbackServices = [
    { id: '1', icon: 'FaTools', title: t('svcInstallTitle'), description: t('svcInstallDesc') },
    { id: '2', icon: 'FaWrench', title: t('svcMaintTitle'), description: t('svcMaintDesc') },
    { id: '3', icon: 'FaCogs', title: t('svcPartsTitle'), description: t('svcPartsDesc') },
    { id: '4', icon: 'FaAward', title: t('svcTrainingTitle'), description: t('svcTrainingDesc') },
    { id: '5', icon: 'FaShippingFast', title: t('svcCustomTitle'), description: t('svcCustomDesc') },
    { id: '6', icon: 'FaHeadset', title: t('svcSupportTitle'), description: t('svcSupportDesc') },
  ];

  useEffect(() => {
    const fetchServices = async () => {
      setLoading(true);
      const data = await getServices();
      setServices(data);
      setLoading(false);
    };
    fetchServices();
  }, []);

  const fadeInUp = {
    hidden: { opacity: 0, y: 30 },
    visible: { opacity: 1, y: 0, transition: { duration: 0.5 } }
  };

  const displayServices = services && services.length > 0 ? services : fallbackServices;

  return (
    <div className="services-page">
      <div className="page-header">
        <div className="banner-particle bp-1" />
        <div className="banner-particle bp-2" />
        <div className="banner-particle bp-3" />
        <div className="container">
          <motion.div initial={{ opacity: 0, y: -20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }}>
            <span className="banner-badge">{t('servicesBadge')}</span>
            <h1>{t('servicesTitle1')} <span className="highlight-red">{t('servicesTitle2')}</span></h1>
          </motion.div>
          <motion.p initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5, delay: 0.2 }}>
            {t('servicesSubtitle')}
          </motion.p>
        </div>
      </div>

      <section className="section bg-light">
        <div className="container">
          {loading ? (
            <div className="loading-container">
              <div className="spinner"></div>
            </div>
          ) : (
            <motion.div 
              className="services-grid"
              initial="hidden"
              animate="visible"
              variants={{
                visible: { transition: { staggerChildren: 0.1 } }
              }}
            >
              {displayServices.map((service) => (
                <motion.div key={service.id} variants={fadeInUp} className="service-card glass">
                  <div className="service-icon-wrapper">
                    {getIcon(service.icon)}
                  </div>
                  <h3>{getServiceTitle(service)}</h3>
                  <p>{getServiceDesc(service)}</p>
                </motion.div>
              ))}
            </motion.div>
          )}
        </div>
      </section>
    </div>
  );
};

export default Services;
